import styled, { css, keyframes } from 'styled-components';

import { Text } from '../../../../components/Text/Text';
import { ANIM, ART_STYLE, COLORS, MOBILE, REDUCED_MOTION, SPACE, STATUS_COLORS } from '../../../../theme/tokens';

/** The samples column of the page's shared ingredients + samples container. */
export const Root = styled.section`
  min-width: 0;
`;

export const Head = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${SPACE[16]};
  margin-bottom: ${SPACE[16]};

  ${MOBILE} {
    flex-direction: column;
    align-items: stretch;
    gap: ${SPACE[12]};
  }
`;

export const HeadCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[4]};
  min-width: 0;
`;

export const Lead = styled(Text)`
  font-size: 13px;
  line-height: 1.45;
  color: ${COLORS.textMuted};
`;

/** The samples are only for judging the style: a quiet line says they never reach the site. */
export const PreviewNote = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${SPACE[4]};
  font-size: 12px;
  line-height: 1.45;
  color: ${COLORS.textFaint};

  svg {
    flex: none;
  }
`;

/*
 * The hero spans the top row; the promotion tile, social story and reward share the second row with
 * column widths proportional to their shapes (1 : 9/16 : 1), so all three stand the same height. On a phone
 * the square tiles pair up and the story sits beneath them at half width.
 */
export const Grid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 0.5625fr) minmax(0, 1fr);
  grid-template-areas:
    'hero hero hero'
    'promo story reward';
  gap: ${SPACE[12]};

  ${MOBILE} {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    grid-template-areas:
      'hero hero'
      'promo reward'
      'story story';
  }
`;

export const Card = styled.div<{ $area: string }>`
  grid-area: ${({ $area }) => $area};
  display: flex;
  flex-direction: column;
  gap: ${SPACE[8]};
  min-width: 0;

  ${({ $area }) =>
    $area === 'story'
      ? css`
          ${MOBILE} {
            justify-self: center;
            /* Half the row less half the gap: the story matches a square tile's width above it. */
            width: calc(50% - 6px);
          }
        `
      : ''}
`;

export const Media = styled.div<{ $ratio: string }>`
  position: relative;
  aspect-ratio: ${({ $ratio }) => $ratio};
  border-radius: 12px;
  overflow: hidden;
  background: ${COLORS.surfaceInset};
  border: 1px solid ${COLORS.hairline};
`;

export const Img = styled.img<{ $loaded: boolean }>`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  opacity: ${({ $loaded }) => ($loaded ? 1 : 0)};
  transition: opacity ${ART_STYLE.SAMPLE_FADE_MS}ms ${ANIM.EASE};

  ${REDUCED_MOTION} {
    transition: none;
  }
`;

export const Placeholder = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${SPACE[4]};
  padding: ${SPACE[8]};
  color: ${COLORS.textFaint};
  text-align: center;
`;

export const PlaceholderText = styled(Text)`
  font-size: 11.5px;
`;

const overlayIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

export const Overlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${overlayIn} ${ART_STYLE.SAMPLE_FADE_MS}ms ${ANIM.EASE} both;

  ${REDUCED_MOTION} {
    animation: none;
  }
`;

/* Over an earlier sample the backdrop lets it show faintly through; with nothing drawn yet it is solid. */
export const Backdrop = styled.div<{ $solid: boolean }>`
  position: absolute;
  inset: 0;
  background: ${COLORS.surfaceCard};
  opacity: ${({ $solid }) => ($solid ? 1 : 0.82)};
`;

export const OverlayCenter = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${SPACE[4]};
  padding: ${SPACE[8]};
  max-width: 100%;
  box-sizing: border-box;
  text-align: center;
  color: ${COLORS.textMuted};
  /* Protect the copy against the dot field behind it (the composer's EasyOutputHint knockout): stacked halos in the
     backdrop's own colour build a clean bed right around each glyph and feather out, inherited by the status line. */
  text-shadow:
    0 0 3px ${COLORS.surfaceCard},
    0 0 3px ${COLORS.surfaceCard},
    0 0 6px ${COLORS.surfaceCard},
    0 0 6px ${COLORS.surfaceCard},
    0 0 10px ${COLORS.surfaceCard},
    0 0 14px ${COLORS.surfaceCard},
    0 0 18px ${COLORS.surfaceCard};
`;

export const OverlayText = styled(Text)`
  font-size: 12px;
  line-height: 1.35;
  color: ${COLORS.text};
`;

export const RetryButton = styled.button`
  margin-top: ${SPACE[4]};
  padding: ${SPACE[4]} ${SPACE[12]};
  border-radius: 999px;
  border: 1px solid ${COLORS.ctaBorder};
  background: ${COLORS.ctaOutlineBg};
  color: ${COLORS.ctaText};
  font-size: 12px;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

export const Versions = styled.div`
  position: absolute;
  bottom: ${SPACE[8]};
  left: 50%;
  transform: translateX(-50%);
  z-index: 2;
  display: inline-flex;
  align-items: center;
  gap: ${SPACE[2]};
  padding: ${SPACE[2]};
  border-radius: 999px;
  background: ${COLORS.overlaySoft};
  backdrop-filter: blur(6px);
`;

export const VersionArrow = styled.button<{ $flip?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  border-radius: 999px;
  border: none;
  background: transparent;
  color: ${COLORS.textBright};
  cursor: pointer;

  svg {
    transform: ${({ $flip }) => ($flip ? 'scaleX(-1)' : 'none')};
  }

  &:disabled {
    cursor: default;
    opacity: 0.35;
  }
`;

export const VersionCount = styled.span`
  min-width: 26px;
  text-align: center;
  font-size: 11px;
  font-variant-numeric: tabular-nums;
  color: ${COLORS.textBright};
`;

export const Foot = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${SPACE[4]} ${SPACE[8]};
  min-width: 0;
`;

export const SlotName = styled(Text)`
  font-size: 13px;
  color: ${COLORS.text};
`;

export const SlotMeta = styled(Text)`
  font-size: 11.5px;
  color: ${COLORS.textFaint};
`;

